import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CnpjLookupButton, formatCnpj } from "@/components/CnpjLookupButton";

const EMPTY = { name: "", document: "", email: "", phone: "", address: "", notes: "" };

export default function Suppliers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => api.get("/suppliers").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s }); setEditId(s.id); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Informe o nome do fornecedor");
    try {
      if (editId) await api.patch(`/suppliers/${editId}`, form);
      else await api.post("/suppliers", form);
      setOpen(false);
      toast.success(editId ? "Fornecedor atualizado" : "Fornecedor cadastrado");
      setTimeout(load, 350);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (id) => {
    if (!confirm("Excluir este fornecedor?")) return;
    await api.delete(`/suppliers/${id}`);
    toast.success("Excluído"); load();
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Compras</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Fornecedores</h1>
        </div>
        <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="new-supplier-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Fornecedor
        </Button>
      </div>

      <Card className="card-outline">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nome</TableHead><TableHead>CNPJ/CPF</TableHead>
              <TableHead>Contato</TableHead><TableHead>Endereço</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                  <TableCell className="font-medium flex items-center gap-2">
                    <Truck className="w-4 h-4 text-slate-400" /> {s.name}
                  </TableCell>
                  <TableCell>{s.document || "—"}</TableCell>
                  <TableCell className="text-sm">{s.email || ""} {s.phone ? `• ${s.phone}` : ""}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.address || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-sup-${s.id}`}><Edit className="w-4 h-4" /></Button>
                    {user?.role === "admin" && (
                      <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => remove(s.id)} data-testid={`del-sup-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Nenhum fornecedor cadastrado.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle className="font-display">{`${editId ? "Editar" : "Novo"} Fornecedor`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sup-name" /></div>
            <div><Label>CNPJ/CPF</Label>
              <div className="flex gap-1">
                <Input value={form.document} onChange={(e) => setForm({ ...form, document: e.target.value })} data-testid="sup-doc" placeholder="CNPJ: busca automática" />
                <CnpjLookupButton value={form.document} testId="sup-cnpj-lookup" onResult={(d) => setForm((f) => ({
                  ...f, name: d.trade_name || d.name || f.name, document: formatCnpj(d.cnpj),
                  email: d.email || f.email, phone: d.phone || f.phone, address: d.address || f.address,
                  notes: f.notes || (d.trade_name && d.name ? `Razão social: ${d.name}` : ""),
                }))} />
              </div></div>
            <div><Label>Telefone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="sup-phone" /></div>
            <div className="col-span-2"><Label>E-mail</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sup-email" /></div>
            <div className="col-span-2"><Label>Endereço</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="sup-addr" /></div>
            <div className="col-span-2"><Label>Observações</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="sup-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-supplier-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
